import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordStrength, isStrongPassword } from '../components/PasswordStrength';
import { Loader2, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin + '/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(password)) { toast.error('Senha nao atende aos requisitos minimos'); return; }
    if (password !== confirmPassword) { toast.error('As senhas nao coincidem'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-white rounded-2xl shadow-lg border p-8 max-w-md w-full text-center">
          <KeyRound className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Link invalido</h2>
          <p className="text-muted-foreground mb-4">Este link de redefinicao de senha e invalido ou ja foi utilizado.</p>
          <Link to="/login"><Button>Voltar ao Login</Button></Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-white rounded-2xl shadow-lg border p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Senha redefinida!</h2>
          <p className="text-muted-foreground mb-4">Sua senha foi alterada com sucesso. Faca login com sua nova senha.</p>
          <Button onClick={() => navigate('/login')} data-testid="go-login-btn">Ir para Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-white rounded-2xl shadow-lg border p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <KeyRound className="w-10 h-10 mx-auto text-primary mb-3" />
          <h2 className="text-2xl font-heading font-bold">Redefinir Senha</h2>
          <p className="text-muted-foreground text-sm mt-1">Crie uma senha forte para sua conta</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nova senha</Label>
            <PasswordInput id="new-password" placeholder="Digite sua nova senha" value={password}
              onChange={(e) => setPassword(e.target.value)} required data-testid="new-password-input" />
            <PasswordStrength password={password} />
          </div>
          <div className="space-y-2">
            <Label>Confirmar senha</Label>
            <PasswordInput id="confirm-password" placeholder="Repita a senha" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-500">As senhas nao coincidem</p>
            )}
          </div>
          <Button type="submit" disabled={loading || !isStrongPassword(password)} className="w-full" data-testid="reset-submit-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redefinindo...</> : 'Redefinir Senha'}
          </Button>
        </form>
      </div>
    </div>
  );
}
